import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  addProcurementPurchaseRequestOffer,
  uploadProcurementPurchaseRequestAttachment,
} from 'api/procurement.api'
import { Card, CardBody } from 'components/Card/Card'
import { FileInput, Input, Label } from 'components/Form'
import { UploadedFile } from 'components/Form/UploadedFile'
import { Spinner } from 'components/Loading/Loading'
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from 'components/ModalV2/Modal'
import { AsyncAutocomplete } from 'components/Select/AsyncAutocomplete'
import { StateTabProvider } from 'components/Tabs/StateTabContext'
import { Tabs } from 'components/Tabs/Tabs'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { Button, colors, ColorVariant, Flex } from 'styling/GlobalStyles'
import { BigText, MediumText, NormalText, SmallText } from 'styling/TypographyV2'
import { IInventoryItemSingleResponse } from 'types/api/inventoryItems.interface'
import {
  AddProcurementPurchaseRequestOfferRequest,
  ProcurementPurchaseRequestItemResponse,
  ProcurementQuoteType,
} from 'types/procurement'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'

enum Tab {
  Manual = 'Manual',
  UploadOffer = 'Upload Offer',
}
const tabs = [Tab.Manual, Tab.UploadOffer]

export const AddOfferModal = ({
  id,
  items,
  onClose,
}: {
  id: number | string
  items: ProcurementPurchaseRequestItemResponse[]
  onClose: () => void
}) => {
  const [data, setData] = useState<Partial<AddProcurementPurchaseRequestOfferRequest>>({
    supplierName: '',
    currency: '',
    supplierId: null,
    deliveryTimeInDays: null,
  })
  const [document, setDocument] = useState<File | null>(null)
  const [itemsWithPrices, setItemsWithPrices] = useState<{ id: number; price: number | null }[]>([
    ...items.map((item) => ({ id: item.id, price: null })),
  ])
  const queryClient = useQueryClient()

  const handleSubmit = async (type: ProcurementQuoteType) => {
    if (!data.supplierName || !data.currency) {
      throw new Error('All fields are required')
    }

    if (data.supplierId) {
      delete data.supplierName
    }

    data.type = type

    if (type === ProcurementQuoteType.Document) {
      if (!document) {
        throw new Error('Document is required')
      }
      const resp = await addProcurementPurchaseRequestOffer(
        id,
        data as AddProcurementPurchaseRequestOfferRequest,
      )
      if (document) {
        const formData = new FormData()
        formData.append('file', document)
        await uploadProcurementPurchaseRequestAttachment(id, resp.id, formData)
      }
    } else {
      data.items = itemsWithPrices.map((item) => ({
        procurementPurchaseRequestItemId: item.id,
        price: item.price,
      }))
      const resp = await addProcurementPurchaseRequestOffer(
        id,
        data as AddProcurementPurchaseRequestOfferRequest,
      )
    }
    toast.success('Offer added successfully')
    queryClient.invalidateQueries({ queryKey: ['procurement-details', id] })
  }

  const mutation = useMutation({
    mutationFn: handleSubmit,
    onSuccess: () => {
      onClose()
    },
    onError: (e) => {
      handleErrors(e)
      toast.error('Failed to add offer')
    },
  })

  return (
    <>
      <Spinner loading={mutation.isPending} />
      <Modal onClose={onClose}>
        <StateTabProvider tabs={tabs}>
          {(activeTab) => (
            <ModalContent>
              <ModalHeader>
                <BigText>Create Supplier Offer</BigText>
              </ModalHeader>
              <ModalBody>
                <Flex column gap='1rem'>
                  <AsyncAutocomplete<{
                    id: number
                    name: string
                  }>
                    inputValue={data.supplierName}
                    setInputValue={(name) => setData({ ...data, supplierName: name })}
                    loadOptions={async (search) => {
                      const data = await baseUrl.get('/inventory-suppliers', {
                        params: {
                          q: search,
                          page: 1,
                          limit: 20,
                        },
                      })
                      return data.data.data
                    }}
                    decoder={(item) => ({
                      label: item.name,
                      value: item.id,
                    })}
                    onChange={(value) => {
                      setData({ ...data, supplierId: value.id, supplierName: value.name })
                    }}
                    value={
                      data.supplierId ? { label: data.supplierName, value: data.supplierId } : null
                    }
                    label='Supplier name'
                    placeholder='Enter supplier name'
                    width='100%'
                  />
                  <Input
                    label='Currency'
                    name='currency'
                    placeholder='Enter currency'
                    value={data.currency}
                    onChange={(e) => setData({ ...data, currency: e.target.value })}
                  />
                  <Input
                    label='Delivery Time (days)'
                    name='deliveryTime'
                    type='number'
                    placeholder='Enter delivery time'
                    value={data.deliveryTimeInDays}
                    onChange={(e) =>
                      setData({ ...data, deliveryTimeInDays: parseInt(e.target.value) })
                    }
                  />
                  <Tabs fullWidth />

                  {activeTab === Tab.UploadOffer &&
                    (document ? (
                      <UploadedFile file={document} />
                    ) : (
                      <FileInput
                        label='Upload Offer'
                        name='offer'
                        placeholder='Upload offer'
                        uploadFile={(files) => setDocument(files[0])}
                      />
                    ))}
                  {activeTab === Tab.Manual && (
                    <Flex column gap='1rem'>
                      <Label>Items</Label>
                      {items.map((item, index) => (
                        <ItemRow
                          key={item.id}
                          item={item}
                          price={itemsWithPrices.find((i) => i.id === item.id)?.price || null}
                          setPrice={(price) =>
                            setItemsWithPrices(
                              itemsWithPrices.map((i) => (i.id === item.id ? { ...i, price } : i)),
                            )
                          }
                          index={index}
                        />
                      ))}
                    </Flex>
                  )}
                </Flex>
              </ModalBody>
              <ModalFooter>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  orange
                  onClick={() =>
                    mutation.mutate(
                      activeTab === Tab.UploadOffer
                        ? ProcurementQuoteType.Document
                        : ProcurementQuoteType.Manual,
                    )
                  }
                >
                  Continue
                </Button>
              </ModalFooter>
            </ModalContent>
          )}
        </StateTabProvider>
      </Modal>
    </>
  )
}

export const ItemRow = ({
  item,
  price,
  setPrice,
  index,
}: {
  item: ProcurementPurchaseRequestItemResponse
  price: number | null
  setPrice: (price: number | null) => void
  index: number
}) => {
  return (
    <Card key={item.id} borderLeft={colors.orange}>
      <CardBody>
        <Flex between>
          <Flex gap='0.5rem'>
            <MediumText colorVariant={ColorVariant.SECONDARY}>#{index + 1}</MediumText>
            <MediumText>{item.inventoryItem ? item.inventoryItem.name : item.name}</MediumText>
          </Flex>
        </Flex>
        <Flex gap='1rem' center mt='12' between>
          <Flex center gap='1rem'>
            <Input
              name='price'
              type='number'
              width='70px'
              placeholder='12'
              value={price}
              onChange={(e) => setPrice(parseInt(e.target.value))}
              noPaddingRight
              inputSize='small'
              align='right'
            />
            <MediumText colorVariant={ColorVariant.SECONDARY}>x{item.quantity}</MediumText>
          </Flex>
          <Flex center gap='0.25rem'>
            <SmallText colorVariant={ColorVariant.SECONDARY}>= Total:</SmallText>
            <NormalText>{price * item.quantity}</NormalText>
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  )
}
